// TicketPanel — the ONE ticket context panel (ADR-036 §2): the standard one-level
// peek the global PeekHost renders on `?peek=tickets:<id>` (through TicketPeek) —
// the hub's walk steps it from row to row (hooks/useRowWalk), never a second
// panel docked beside the table. Header = the star seat (never a class glyph) · the eyebrow
// "Capture · #n" with the ticket's own copiable ref · the site name · the
// stage badge; the body is TicketCard; the footer is the labelled Actions
// door over the filing verbs — the ticket's registry (`TicketActions.tsx`: Close ·
// Reopen · "Nothing to classify, close" for a holder of BOTH keys — ADR-073 §2). Peek-only at this phase: no full page, so no
// "Open full page" commit. The review walk is the HOST's (the peek wraps it and
// moves on to the walk's next row at the last answer) — this panel only asks
// for it (`onReview`, the button and R).

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LibraryFile as WireFile } from '@ems-app/schemas/files/responses';
import { TICKET_CLOSE_REFUSALS, type TicketDetail } from '@ems-app/schemas/tickets/responses';
import { ContextPanel } from '../../components/patterns/ContextPanel.js';
import { EntityRef } from '../../components/patterns/EntityRef.js';
import { resolveActs } from '../../components/patterns/entityRegistry.js';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { DescriptionList, Field } from '../../components/patterns/DescriptionList.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { formatDate, formatNumber, zoneName } from '../../lib/format.js';
import { FileTile, useTopicLabel, type LibraryFile } from '../../components/patterns/filesDisplay.js';
import { FollowStar } from '../../components/patterns/FollowStar.js';
import { ObservationCard } from '../../components/patterns/observations.js';
import { LOCATION_KIND_ICON, TicketStatusBadge, VisitFace, ticketNumberText, ticketWhatText } from '../../components/patterns/ticketsDisplay.js';
import { Alert } from '../../components/ui/Alert.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { InlineCopy } from '../../components/ui/CopyField.js';
import { Link } from '../../components/ui/Link.js';
import { PageLoader } from '../../components/ui/PageLoader.js';
import { useToast } from '../../components/ui/Toast.js';
import { icons } from '../../components/ui/icons.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { fetchWaitingFileIds, useClassifyFiles, useMedia, usePreviewLinks, type PreviewWant } from '../../hooks/useFiles.js';
import { useFollowTicket } from '../../hooks/useFollows.js';
import { ticketsKeys, useCloseTicket, useReopenTicket, useTicketDetail } from '../../hooks/useTickets.js';
import { useUrlOverlay } from '../../hooks/useUrlOverlay.js';
import { useEntityPeek } from '../../hooks/useEntityPeek.js';
import { ticketActs } from './TicketActions.js';
import { TicketFileSurface } from './TicketFileSurface.js';
import { apiErrorCode } from '../../lib/apiError.js';
import { isEditable, modalOwnsKeys } from '../../lib/keyOwner.js';
import { useRefusalToast } from '../../components/patterns/forms/useQuickSave.js';

/** The card shows this many thumbs, then "+n" (the whole set is the library's). */
const EVIDENCE_TILES = 6;
/** The bulk answer's cap per request (the wire's). */
const BULK_MAX = 100;
/** The refusals the panel names in the ticket's words: the close family, and the reopen's own. */
const NAMED_REFUSALS: readonly string[] = [...TICKET_CLOSE_REFUSALS, 'TICKET_NOT_CLOSED'];

export interface TicketPanelProps {
  projectId: string;
  ticketId: string;
  /** Close (X · Escape · Back). */
  onClose: () => void;
  /** "Review n photos" (the button, and R) — the host opens the walk. */
  onReview: () => void;
}

export function TicketPanel({ projectId, ticketId, onClose, onReview }: TicketPanelProps) {
  const { t } = useTranslation('tickets');
  const toast = useToast();
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const canReview = can('files:review');
  const detail = useTicketDetail(projectId, ticketId);
  const ticket = detail.data;
  // One clock per data arrival — the read's own timestamp — so every face on
  // the card reads the same "now" and a re-render never drifts a span.
  const now = detail.dataUpdatedAt;

  const follow = useFollowTicket(projectId);
  const close = useCloseTicket(projectId);
  const reopen = useReopenTicket(projectId);
  const classifyFiles = useClassifyFiles(projectId);
  const [confirmNothing, setConfirmNothing] = useState(false);
  // A tile opens the ticket's file surface OVER the panel — never a navigation to the
  // library (Escape and Back must return to the peek, wherever it was opened from).
  // The library's own slot, one history step (`useUrlOverlay`): the first open pushes, so Back closes the photo and
  // returns to the peek; a step to another file replaces; the close gives the step back.
  const { value: openFileId, open: openFile, close: closeFile } = useUrlOverlay('file');
  // The ticket's site, where the viewer may open it: the link is FOLLOWED — the ticket joins the way back, named as
  // its eyebrow names it (the class word and the number — ADR-073 §5).
  const { followPeek } = useEntityPeek();
  const peekSite = (siteId: string) => {
    if (ticket) followPeek(`sites:${siteId}`, `${t(`class.${ticket.class}`)} ${ticketNumberText(ticket.number)}`);
  };

  const qc = useQueryClient();
  // A refusal speaks in the ticket's words (the named 409s — ADR-073 §2 — and the reopen's own, with the ticket's own
  // count); anything else is the one refusal toast. One key: a second refusal replaces the first.
  const refused = useRefusalToast('ticket');
  const fail = (err: unknown): void => {
    const code = apiErrorCode(err);
    if (code && NAMED_REFUSALS.includes(code)) toast.error(t(`refusal.${code}`, { count: ticket?.waiting_count ?? 0 }), { key: 'ticket' });
    else refused(err);
  };

  // R opens the walk — the peek's own key wherever it opens and at any width (its own sheet never stands it down;
  // a dialog over it does — lib/keyOwner).
  const reviewable = canReview && ticket?.stage === 'to_review';
  useEffect(() => {
    if (!reviewable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isEditable(e.target)) return;
      if (modalOwnsKeys('peek')) return;
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        onReview();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reviewable, onReview]);

  const nothingThenClose = async () => {
    if (!ticket) return;
    try {
      const ids = await fetchWaitingFileIds(projectId, `ticket:${ticket.id}`);
      for (let i = 0; i < ids.length; i += BULK_MAX) {
        await classifyFiles.mutateAsync({ file_ids: ids.slice(i, i + BULK_MAX), decision: 'nothing', assets: [], topics: [] });
      }
      await close.mutateAsync({ ticketId });
      toast.success(t('toast.closed'));
    } catch (err) {
      // A chunk may have landed before the failure: the ticket's counts refetch, never a stale "n await review".
      void qc.invalidateQueries({ queryKey: ticketsKeys.all(projectId) });
      fail(err);
    }
  };

  // The filing verbs — the ticket's registry (`TicketActions.tsx`), the panel's door.
  const acts = ticket
    ? resolveActs(
        ticketActs(
          {
            close: () => void close.mutateAsync({ ticketId }).then(() => toast.success(t('toast.closed')), fail),
            reopen: () => void reopen.mutateAsync({ ticketId }).then(() => toast.success(t('toast.reopened')), fail),
            nothingClose: () => setConfirmNothing(true),
          },
          { canReview, completed: ticket.stage === 'completed' },
        ),
        { can, entitlements, status: ticket.stage },
        (key) => t(key),
      )
    : [];

  const header = ticket
    ? {
        leading: <FollowStar followed={ticket.followed} onToggle={() => follow.mutate({ ticketId, followed: !ticket.followed }, { onError: fail })} size={20} />,
        kind: (
          <span className="inline-flex items-center gap-1.5">
            {t(`class.${ticket.class}`)}
            <span aria-hidden>·</span>
            <InlineCopy value={ticketNumberText(ticket.number)} iconAlways />
          </span>
        ),
        title: ticket.location.name,
        status: <TicketStatusBadge item={ticket} t={t} />,
      }
    : { title: '…' };

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        {...header}
        acts={acts}
      >
        {/* A LIVE read: the card under the person's eyes is never swapped for an
            error because a background re-read failed — the last answer stays. */}
        {detail.isPending ? (
          <PageLoader />
        ) : ticket ? (
          <TicketCard projectId={projectId} ticket={ticket} now={now} canReview={canReview} onReview={onReview} onOpenFile={openFile} onPeekSite={peekSite} />
        ) : detail.isError ? (
          <Alert variant="danger">{t('error')}</Alert>
        ) : null}
      </ContextPanel>
      {ticket && openFileId && <TicketFileSurface mode="browse" projectId={projectId} ticket={ticket} canReview={canReview} fileId={openFileId} onOpen={openFile} onClose={closeFile} />}
      {ticket && (
        <ConfirmDialog
          open={confirmNothing}
          onClose={() => setConfirmNothing(false)}
          title={t('actions.nothingClose')}
          description={t('actions.nothingCloseConsequence', { count: ticket.waiting_count })}
          confirmLabel={t('actions.nothingClose')}
          tone="default"
          onConfirm={nothingThenClose}
        />
      )}
    </>
  );
}

// ── the card — the panel's body ──────────────────────────────────────────────

function TicketCard({
  projectId,
  ticket,
  now,
  canReview,
  onReview,
  onOpenFile,
  onPeekSite,
}: {
  projectId: string;
  ticket: TicketDetail;
  now: number;
  canReview: boolean;
  onReview: () => void;
  onOpenFile: (id: string) => void;
  /** The site's peek — followed inside the sheet. */
  onPeekSite: (siteId: string) => void;
}) {
  const { t, i18n } = useTranslation('tickets');
  const topicLabel = useTopicLabel();
  // The peek's clocks (ADR-082 §2): each moment in PROJECT time (the peek has no host screen — it opens from the hub,
  // a site tab, a link), the viewer's own beside it only where the two clocks READ differently (Beirut and Riyadh
  // agree all summer — nothing to say then); the visits in their SITE's time.
  const display = useDisplayZone({ kind: 'project', projectId });
  const zone = display.zone ?? display.viewerZone ?? 'UTC';
  const siteZone = ticket.location.timezone_iana;
  const moment = (iso: string) => formatDate(iso, 'datetime', { timeZone: zone }) ?? '';
  const yours = (iso: string) => {
    if (!display.word || !display.viewerZone) return undefined;
    const own = formatDate(iso, 'datetime', { timeZone: display.viewerZone }) ?? '';
    return own === moment(iso) ? undefined : t('card.yourTime', { time: own });
  };
  const anchor = `ticket:${ticket.id}`;
  const loc = ticket.location;
  const KindIcon = LOCATION_KIND_ICON[loc.kind];
  const kindWord = t(`kind.${loc.kind}`);
  const siteFiles = loc.kind === 'site' && loc.id ? `/p/${projectId}/sites/${loc.id}/files` : null;

  // Evidence — the files media read on the ticket anchor (the handful the card shows, flat, newest first — a live
  // look-again re-reads these six rows alone; the rest is the server's count), thumbs from the batched mint.
  const media = useMedia(projectId, { anchor }, { review: false, sort: 'newest', group: null, page: 1, limit: EVIDENCE_TILES });
  const wire = useMemo<WireFile[]>(() => (media.data?.items ?? []).slice(0, EVIDENCE_TILES), [media.data]);
  const wanted = useMemo<PreviewWant[]>(
    () => wire.filter((f) => f.family === 'image' && f.derivatives.some((d) => d.kind === 'thumb' && d.ready)).map((f) => ({ file_id: f.id, kind: 'thumb' })),
    [wire],
  );
  const links = usePreviewLinks(projectId, wanted);
  const tiles = useMemo<LibraryFile[]>(() => wire.map((f) => ({ ...f, thumb: links[`${f.id}:thumb`] ?? null, fresh: false })), [wire, links]);
  const more = ticket.evidence_count - tiles.length;
  const what = ticketWhatText(ticket, topicLabel, t);

  return (
    <>
      {/* The location context line — the hero grammar (kind icon sr-worded · place · the copiable code). */}
      <div className="flex min-w-0 items-center gap-1.5 text-sm text-[var(--color-text-primary)]">
        <span className="inline-flex shrink-0 text-[var(--color-text-secondary)]" title={kindWord}>
          <KindIcon size={14} aria-hidden />
          <span className="sr-only">{kindWord}</span>
        </span>
        {/* ONE reference among facts — the blue chip, where the server says the viewer may open the site (ADR-036 §6:
            a creator reads their own ticket at a site outside their reach — the name then stays a fact). */}
        {loc.kind === 'site' && loc.id && loc.can_open ? (
          <EntityRef name={loc.name} to={`/p/${projectId}/sites/${loc.id}`} onPeek={() => onPeekSite(loc.id!)} />
        ) : (
          <span className="truncate">{loc.name}</span>
        )}
        {loc.reference && (
          <>
            <span aria-hidden className="text-[var(--color-text-secondary)]">·</span>
            <InlineCopy value={loc.reference} iconAlways />
          </>
        )}
      </div>

      {/* What waits — the peek's ONE tint (ADR-061): the observation card with
          the walk as its action; the other stages say their one quiet line. */}
      {ticket.stage === 'to_review' && (
        <ObservationCard
          kind="unverified"
          impact="medium"
          kindLabel={t('stage.to_review')}
          subject={ticket.person ? t('card.waits', { count: ticket.waiting_count, who: ticket.person.label }) : t('card.waitsNoPerson', { count: ticket.waiting_count })}
          actions={
            canReview ? (
              <Button variant="secondary" onClick={onReview}>
                {t('card.review', { count: ticket.waiting_count })}
                <span aria-hidden className="ms-2 rounded border border-[var(--color-border)] px-1 text-xs text-[var(--color-text-secondary)]">
                  {t('card.reviewKey')}
                </span>
              </Button>
            ) : (
              // Without the review key the doors are named (ADR-073 §2): the walk is a reviewer's, the policy a setting's.
              <p className="m-0 text-sm text-[var(--color-text-secondary)]">{t('refusal.PHOTOS_AWAIT_REVIEW_doors')}</p>
            )
          }
        />
      )}
      {ticket.stage === 'in_progress' && <p className="text-sm text-[var(--color-text-secondary)]">{t('card.arriving')}</p>}
      {(ticket.stage === 'completed' || ticket.stage === 'closed') && <p className="text-sm text-[var(--color-text-secondary)]">{t('card.nothingWaits')}</p>}

      <DetailSection dense title={ticket.visits.length > 1 ? t('card.visits') : t('card.visit')}>
        {ticket.visits.length === 0 ? (
          <VisitFace visit={null} now={now} t={t} zone={siteZone ?? zone} />
        ) : (
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {ticket.visits.map((v) => (
              <li key={v.id}>
                {ticket.visits.length > 1 && v.person && <div className="mb-0.5 text-xs text-[var(--color-text-secondary)]">{v.person.label}</div>}
                <VisitFace visit={v} now={now} t={t} zone={siteZone ?? zone} siteTime={!!siteZone && siteZone !== zone} />
              </li>
            ))}
          </ol>
        )}
      </DetailSection>

      {/* Time — the three moments in the project's zone, named once; the viewer's own clock beside each when it differs. */}
      <DetailSection dense title={t('card.time')} action={<span className="text-xs text-[var(--color-text-secondary)]">{zoneName(zone, i18n.language)}</span>}>
        <DescriptionList divided={false}>
          <Field label={t('time.tip.opened')} detail={yours(ticket.created_at)}>
            {moment(ticket.created_at)}
          </Field>
          {ticket.completed_at && (
            <Field label={t('time.tip.done')} detail={yours(ticket.completed_at)}>
              {moment(ticket.completed_at)}
            </Field>
          )}
          {ticket.closed_at && (
            <Field label={t('time.tip.closed')} detail={yours(ticket.closed_at)}>
              {moment(ticket.closed_at)}
            </Field>
          )}
        </DescriptionList>
      </DetailSection>

      <DetailSection dense title={t('card.evidence')} action={ticket.evidence_count > 0 ? <span className="text-sm text-[var(--color-text-secondary)] tabular-nums">{formatNumber(ticket.evidence_count)}</span> : undefined}>
        {tiles.length === 0 ? (
          <p className="text-sm text-[var(--color-text-secondary)]">—</p>
        ) : (
          <div className="grid grid-cols-3 gap-1.5">
            {tiles.map((f) => (
              <FileTile key={f.id} file={f} context="none" onSelect={(file) => onOpenFile(file.id)} />
            ))}
            {more > 0 && (
              <span className="flex aspect-square items-center justify-center rounded-md border border-dashed border-[var(--color-border)] text-xs tabular-nums text-[var(--color-text-secondary)]">
                {t('what.more', { count: more })}
              </span>
            )}
          </div>
        )}
      </DetailSection>

      <DetailSection dense title={t('card.what')}>
        {what ? (
          <p className="text-sm text-[var(--color-text-primary)]">{what}</p>
        ) : (
          <p className="text-sm text-[var(--color-text-disabled)]">{t('what.notClassified')}</p>
        )}
      </DetailSection>

      {siteFiles && (
        <Link to={siteFiles} className="inline-flex items-center gap-1.5 self-start text-sm">
          <icons.folder size={16} aria-hidden />
          {t('card.library')}
        </Link>
      )}
    </>
  );
}
